/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useMemo } from "react";
import useMenuStore from "stores/menuStore";
import useAuthStore from "stores/authStore";
import useLayoutStore from "stores/layoutStore";
import classNames from "classnames";
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";
import { getMenuItems } from "routes/guards";
import { useWindowSize } from "hooks/useWindowSize";
import { useWindowScroll } from "hooks/useWindowScroll";
import menuConfig from "routes/menu-config";
import { MENU_PLACEMENT, MENU_BEHAVIOUR } from "../../../utils/constant";
import MainMenuItems from "./MainMenuItems";
import {
  checkBehaviour,
  checkPlacement,
  isDeeplyDiffBehaviourStatus,
  isDeeplyDiffPlacementStatus,
} from "./helper";

const MainMenu = () => {
  const {
    placement,
    behaviour,
    placementStatus,
    behaviourStatus,
    attrMobile,
    breakpoints,
    useSidebar,
    menuChangeAttrMenuAnimate,
    menuChangeAttrMobile,
    menuChangeBehaviourStatus,
    menuChangeCollapseAll,
    menuChangeNavClasses,
    menuChangePinButtonEnable,
    menuChangePlacementStatus,
  } = useMenuStore();
  const { isLogin, currentUser } = useAuthStore();
  const { layoutShowingNavMenu } = useLayoutStore();
  const scrolled = useWindowScroll();
  const { width } = useWindowSize();

  const menuItemsMemo = useMemo(
    () =>
      getMenuItems({
        data: attrMobile && useSidebar ? menuConfig : menuConfig.mainMenuItems,
        isLogin,
        userRole: currentUser?.role?.permissions || [],
      }),
    [isLogin, currentUser, attrMobile, useSidebar]
  );

  useEffect(() => {
    menuChangeAttrMenuAnimate("");
    layoutShowingNavMenu("");

    if (placementStatus.status === 2 || placementStatus.status === 4) {
      // Switching back from the mobile menu layout fast
      menuChangeNavClasses({});
      menuChangeAttrMobile(false);
    }
    // Prevents menu animation to make a fast switch
    if (behaviourStatus.status === 1) {
      menuChangeCollapseAll(true);
      menuChangePinButtonEnable(true);
    } else if (behaviourStatus.status === 2) {
      menuChangeCollapseAll(true);
      menuChangePinButtonEnable(false);
    } else if (behaviourStatus.status === 3) {
      menuChangePinButtonEnable(true);
      menuChangeCollapseAll(false);
    } else if (behaviourStatus.status === 4) {
      menuChangePinButtonEnable(false);
      menuChangeCollapseAll(true);
    } else if (behaviourStatus.status === 5) {
      menuChangeCollapseAll(false);
      menuChangePinButtonEnable(true);
    } else if (behaviourStatus.status === 6) {
      menuChangeCollapseAll(false);
      menuChangePinButtonEnable(true);
    }
    // eslint-disable-next-line
  }, [behaviourStatus, placementStatus]);

  useEffect(() => {
    if (
      placementStatus.placementHtmlData === MENU_PLACEMENT.Vertical &&
      behaviourStatus.behaviourHtmlData === MENU_BEHAVIOUR.Unpinned &&
      attrMobile !== true
    ) {
      menuChangeCollapseAll(true);
      menuChangeAttrMenuAnimate("hidden");
    }
    return () => {};
    // eslint-disable-next-line
  }, [attrMobile]);

  useEffect(() => {
    if (
      placementStatus.placementHtmlData === MENU_PLACEMENT.Horizontal &&
      !attrMobile &&
      behaviourStatus.behaviourHtmlData === MENU_BEHAVIOUR.Unpinned
    ) {
      if (scrolled) {
        menuChangeAttrMenuAnimate("hidden");
        // Hiding all dropdowns to make sure they are closed when menu collapses
        document.documentElement.click();
      } else {
        menuChangeAttrMenuAnimate("");
      }
    }
    return () => {};
    // eslint-disable-next-line
  }, [scrolled]);

  const getMenuStatus = useCallback(
    (pBreakpoints, pPlacement, pBehaviour) => {
      if (pBreakpoints) {
        const placementStatusCB = checkPlacement({
          placement: pPlacement,
          breakpoints: pBreakpoints,
        });
        const behaviourStatusCB = checkBehaviour({
          placement: placementStatusCB.placementHtmlData,
          behaviour: pBehaviour,
          breakpoints: pBreakpoints,
        });

        if (isDeeplyDiffPlacementStatus(placementStatusCB, placementStatus)) {
          menuChangePlacementStatus(placementStatusCB);
        }
        if (isDeeplyDiffBehaviourStatus(behaviourStatusCB, behaviourStatus)) {
          menuChangeBehaviourStatus(behaviourStatusCB);
        }
      }
      // eslint-disable-next-line
    },
    [behaviourStatus, placementStatus, breakpoints]
  );

  useEffect(() => {
    if (width && placement && behaviour && breakpoints) {
      getMenuStatus(breakpoints, placement, behaviour);
    }
    // eslint-disable-next-line
  }, [width, breakpoints, placement, behaviour]);

  // Initializes the horizontal menu
  // Customizes dropdown clicks to prevent auto closing and making sure all sub menus are closed when parent is closed
  if (menuItemsMemo) {
    if (placementStatus.view === MENU_PLACEMENT.Horizontal) {
      return (
        <div className="menu-container flex-grow-1">
          <ul id="menu" className={classNames("menu show")}>
            <MainMenuItems
              menuItems={menuItemsMemo}
              menuPlacement={placementStatus.view}
            />
          </ul>
        </div>
      );
    }
    // Vertical menu scrollbar init
    return (
      <OverlayScrollbarsComponent
        options={{
          scrollbars: { autoHide: "leave", autoHideDelay: 600 },
          overflowBehavior: { x: "hidden", y: "scroll" },
        }}
        className="menu-container flex-grow-1"
      >
        <ul id="menu" className={classNames("menu show")}>
          <MainMenuItems
            menuItems={menuItemsMemo}
            menuPlacement={placementStatus.view}
          />
        </ul>
      </OverlayScrollbarsComponent>
    );
  }
  return <></>;
};

export default React.memo(MainMenu);
